import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TablePagination } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import type { IntegrationApi } from '../../connectionTypes';
import { extractErrorMessage } from '../../api/apiErrors';
import { WizardCheckboxRow } from '../components/WizardCheckboxRow';
import { WizardChecklistPanel } from '../components/WizardChecklistPanel';
import { IMPORT_WIZARD_CHECKLIST_PAGE_SIZE } from '../importWizardConstants';
import { useIntegrationCompanies } from '../hooks/useIntegrationCompanies';
import type { IntegrationSelectionState } from '../types';

type SelectIntegrationCompaniesStepProps = {
  baseUrl: string;
  nonce: string;
  connections: IntegrationApi[];
  activeConnectionId: string;
  selectionsByConnectionId: Record<string, IntegrationSelectionState>;
  onToggleRemoteCompany: (connectionId: string, remoteCompanyId: string) => void;
};

type IntegrationCompaniesPanelProps = {
  baseUrl: string;
  nonce: string;
  connection: IntegrationApi;
  connections: IntegrationApi[];
  selectedRemoteCompanyIds: string[];
  onToggleRemoteCompany: (connectionId: string, remoteCompanyId: string) => void;
};

const IntegrationCompaniesPanel = ({
  baseUrl,
  nonce,
  connection,
  connections,
  selectedRemoteCompanyIds,
  onToggleRemoteCompany,
}: IntegrationCompaniesPanelProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSelectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearch('');
    setPage(1);
  }, [connection.id]);

  const query = useIntegrationCompanies({
    baseUrl,
    nonce,
    connectionId: connection.id,
    q: search,
    page,
    perPage: IMPORT_WIZARD_CHECKLIST_PAGE_SIZE,
  });

  const visibleItems = useMemo(() => query.data?.items ?? [], [query.data?.items]);

  const { allVisibleSelected, someVisibleSelected } = useMemo(() => {
    if (visibleItems.length === 0) {
      return { allVisibleSelected: false, someVisibleSelected: false };
    }
    let selectedOnPage = 0;
    for (const item of visibleItems) {
      if (selectedRemoteCompanyIds.includes(item.remote_company_id)) {
        selectedOnPage += 1;
      }
    }
    return {
      allVisibleSelected: selectedOnPage === visibleItems.length,
      someVisibleSelected: selectedOnPage > 0,
    };
  }, [visibleItems, selectedRemoteCompanyIds]);

  useLayoutEffect(() => {
    const el = pageSelectAllRef.current;
    if (!el) {
      return;
    }
    el.indeterminate = visibleItems.length > 0 && someVisibleSelected && !allVisibleSelected;
  }, [visibleItems.length, someVisibleSelected, allVisibleSelected]);

  const togglePageSelection = () => {
    if (visibleItems.length === 0) {
      return;
    }
    if (allVisibleSelected) {
      visibleItems.forEach((item) => {
        if (selectedRemoteCompanyIds.includes(item.remote_company_id)) {
          onToggleRemoteCompany(connection.id, item.remote_company_id);
        }
      });
      return;
    }
    visibleItems.forEach((item) => {
      if (!selectedRemoteCompanyIds.includes(item.remote_company_id)) {
        onToggleRemoteCompany(connection.id, item.remote_company_id);
      }
    });
  };

  const selectAllPageId = `${connection.id}-select-all-page`;

  const statusSlot =
    query.isLoading ? <p>{__('Chargement des entreprises distantes...')}</p> : null;
  const errorSlot =
    query.error !== null ? (
      <div role="alert">
        <p>{extractErrorMessage(query.error, __('Impossible de charger les entreprises distantes.'))}</p>
        <button type="button" className="btn-secondary" onClick={() => void query.refetch()}>
          {__('Réessayer')}
        </button>
      </div>
    ) : null;

  const listBody =
    query.isLoading || query.error !== null ? null : (
      <>
        {visibleItems.map((item) => {
          const inputId = `${connection.id}-${item.remote_company_id}`;
          const statusId = `${inputId}-status`;
          const peerIds = item.peer_connection_ids ?? [];
          const linkIds = item.also_linked_connections ?? [];
          const badgeConnIds: string[] = [];
          const seenBadge = new Set<string>();
          for (const id of [...peerIds, ...linkIds]) {
            if (!seenBadge.has(id)) {
              seenBadge.add(id);
              badgeConnIds.push(id);
            }
          }
          return (
            <li key={item.remote_company_id}>
              <WizardCheckboxRow
                inputId={inputId}
                checked={selectedRemoteCompanyIds.includes(item.remote_company_id)}
                onChange={() => onToggleRemoteCompany(connection.id, item.remote_company_id)}
                label={
                  <>
                    <span>{item.label}</span>
                    <span className="screen-reader-text">{` ${item.remote_company_id}`}</span>
                    {item.linked_company_id ? (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '6px', flexShrink: 0 }}
                      >
                        <path d="M9 13C9.382 13.686 9.921 14.272 10.572 14.71C11.223 15.149 11.968 15.429 12.747 15.526C13.526 15.624 14.317 15.536 15.056 15.271C15.795 15.006 16.461 14.57 17 14L21 10C21.928 9.072 22.45 7.813 22.45 6.5C22.45 5.187 21.928 3.928 21 3C20.072 2.072 18.813 1.55 17.5 1.55C16.187 1.55 14.928 2.072 14 3L12 5M15 11C14.618 10.314 14.079 9.728 13.428 9.29C12.777 8.851 12.032 8.571 11.253 8.474C10.474 8.376 9.683 8.464 8.944 8.729C8.206 8.994 7.539 9.43 7 10L3 14C2.072 14.928 1.55 16.187 1.55 17.5C1.55 18.813 2.072 20.072 3 21C3.928 21.928 5.187 22.45 6.5 22.45C7.813 22.45 9.072 21.928 10 21L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : null}
                    {badgeConnIds.length > 0 ? (
                      <span className="provider-company-badges" aria-label={__('Autres connecteurs')}>
                        {badgeConnIds.map((connId) => {
                          const conn = connections.find((c) => c.id === connId);
                          if (!conn) return null;
                          return (
                            <img
                              key={connId}
                              src={conn.logoUrl}
                              alt={conn.name}
                              title={conn.name}
                              className="provider-company-badge-logo"
                              width={20}
                              height={20}
                              decoding="async"
                            />
                          );
                        })}
                      </span>
                    ) : null}
                  </>
                }
                statusAnnouncement={
                  item.linked_company_id
                    ? { id: statusId, text: __('Déjà lié à un dossier existant') }
                    : undefined
                }
              />
            </li>
          );
        })}
      </>
    );

  return (
    <WizardChecklistPanel
      panelAriaLabel={connection.name}
      searchValue={search}
      searchPlaceholder={__('Rechercher une entreprise')}
      searchAriaLabel={__('Rechercher une entreprise')}
      onSearchChange={(value) => {
        setSearch(value);
        setPage(1);
      }}
      listAriaLabel={__('Entreprises distantes')}
      statusSlot={
        <>
          {statusSlot}
          {errorSlot}
        </>
      }
      selectAll={
        query.isLoading || query.error !== null
          ? undefined
          : {
              id: selectAllPageId,
              inputRef: pageSelectAllRef,
              checked: allVisibleSelected,
              disabled: visibleItems.length === 0,
              label: __('Sélectionner toute la page'),
              onChange: togglePageSelection,
            }
      }
      footer={
        query.isLoading || query.error !== null ? null : (
          <TablePagination
            page={(query.data?.page ?? 1) - 1}
            totalPages={query.data?.total_pages ?? 1}
            onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setPage((prev) => prev + 1)}
            previousPageAriaLabel={__('Page précédente')}
            nextPageAriaLabel={__('Page suivante')}
          />
        )
      }
    >
      {listBody}
    </WizardChecklistPanel>
  );
};

export const SelectIntegrationCompaniesStep = ({
  baseUrl,
  nonce,
  connections,
  activeConnectionId,
  selectionsByConnectionId,
  onToggleRemoteCompany,
}: SelectIntegrationCompaniesStepProps) => {
  if (connections.length === 0) {
    return <p>{__('Aucune connexion active. Configurez une connexion dans Connexions.')}</p>;
  }

  const activeConnection =
    connections.find((connection) => connection.id === activeConnectionId) ?? connections[0];

  return (
    <IntegrationCompaniesPanel
      baseUrl={baseUrl}
      nonce={nonce}
      connection={activeConnection}
      connections={connections}
      selectedRemoteCompanyIds={selectionsByConnectionId[activeConnection.id]?.selectedRemoteCompanyIds ?? []}
      onToggleRemoteCompany={onToggleRemoteCompany}
    />
  );
};
